/** The stream client: bound subscriptions, dense sequences, caps, and snapshot reconciliation (UX05). */
import {
  STREAMS_PER_IDENTITY,
  STREAM_PATH,
  SUBSCRIPTIONS_PER_STREAM,
  TRANSIENT_BUFFER_EVENTS,
} from "../contracts/constants.ts";
import {
  decodeResyncFrame,
  decodeStreamEvent,
  type ResyncReason,
  type StreamEvent,
} from "../contracts/envelope.ts";

export interface EventSourceLike {
  addEventListener(type: string, listener: (event: MessageEvent<string>) => void): void;
  close(): void;
  onerror: ((event: Event) => void) | null;
}

export type EventSourceFactory = (url: string) => EventSourceLike;

/** One bound subscription: what, for whom, which schema, and where to resume. */
export interface Subscription {
  readonly projection: string;
  readonly accountId: string;
  readonly schemaVersion: number;
  readonly snapshotId: string;
  readonly afterWatermark: number;
  /** A replacement projection may coalesce; a command or fill history never does. */
  readonly coalescing: boolean;
}

export type Cause = ResyncReason | "ERROR" | "BAD_FRAME";

export type ConnectionState =
  | { readonly kind: "idle" }
  | { readonly kind: "connecting" }
  | { readonly kind: "live" }
  | { readonly kind: "reconciling"; readonly cause: Cause; readonly attempt: number }
  | { readonly kind: "refused"; readonly reason: string };

/** Counts the transport streams this tab holds against the per-identity cap. */
export class StreamRegistry {
  private open = 0;

  /** Take a slot; `false` when the per-identity cap is reached. */
  acquire(): boolean {
    if (this.open >= STREAMS_PER_IDENTITY) {
      return false;
    }
    this.open += 1;
    return true;
  }

  /** Give a slot back. */
  release(): void {
    this.open = Math.max(0, this.open - 1);
  }

  /** The streams held. */
  get count(): number {
    return this.open;
  }
}

export interface StreamDeps {
  readonly createSource: EventSourceFactory;
  readonly registry: StreamRegistry;
  readonly generation: () => number;
  /** Obtain a consistent snapshot and return the subscriptions positioned after it (`null`: superseded). */
  readonly snapshot: () => Promise<readonly Subscription[] | null>;
  readonly onEvent: (event: StreamEvent) => void;
  readonly onState: (state: ConnectionState) => void;
  /** Told how many events wait for delivery after each is buffered (the UX10 observation). */
  readonly onBuffered?: (events: number) => void;
  readonly schedule: (task: () => void, ms: number) => () => void;
}

const DEDUP_ENTITIES_MAX = 10_000;
const RETRY_BASE_MS = 1000;
const RETRY_MAX_MS = 30_000;

/** The handshake URL: the generation and one `sub` parameter per subscription. */
export function handshakeUrl(generation: number, subscriptions: readonly Subscription[]): string {
  const query = new URLSearchParams({ generation: String(generation) });
  for (const sub of subscriptions) {
    query.append(
      "sub",
      [
        sub.projection,
        sub.accountId,
        String(sub.schemaVersion),
        sub.snapshotId,
        String(sub.afterWatermark),
      ].join(","),
    );
  }
  return `${STREAM_PATH}?${query.toString()}`;
}

interface Position {
  watermark: number;
  readonly coalescing: boolean;
  readonly versions: Map<string, number>;
}

/** Owns one EventSource and keeps it consistent: any gap, error or resync frame reconciles first. */
export class StreamClient {
  private readonly deps: StreamDeps;
  private source: EventSourceLike | null = null;
  private held = false;
  private closed = false;
  private reconciling = false;
  private positions = new Map<string, Position>();
  private pending: StreamEvent[] = [];
  private flushing = false;
  private cancelTimer: (() => void) | null = null;
  private runGeneration = 0;

  constructor(deps: StreamDeps) {
    this.deps = deps;
  }

  /** The events received and not yet delivered (never above the transient cap). */
  get buffered(): number {
    return this.pending.length;
  }

  /** Open the stream for `subscriptions`; refused client-side, with a reason, when a cap would be exceeded. */
  connect(subscriptions: readonly Subscription[]): void {
    if (this.closed) {
      return;
    }
    if (subscriptions.length === 0 || subscriptions.length > SUBSCRIPTIONS_PER_STREAM) {
      this.deps.onState({
        kind: "refused",
        reason: `${String(subscriptions.length)} subscriptions; between 1 and ${String(SUBSCRIPTIONS_PER_STREAM)} are allowed per stream`,
      });
      return;
    }
    if (!this.held) {
      if (!this.deps.registry.acquire()) {
        this.deps.onState({
          kind: "refused",
          reason: `this identity already holds ${String(STREAMS_PER_IDENTITY)} streams`,
        });
        return;
      }
      this.held = true;
    }
    this.runGeneration = this.deps.generation();
    this.positions = new Map(
      subscriptions.map((sub) => [
        sub.projection,
        { watermark: sub.afterWatermark, coalescing: sub.coalescing, versions: new Map() },
      ]),
    );
    this.pending = [];
    this.deps.onState({ kind: "connecting" });
    const source = this.deps.createSource(handshakeUrl(this.runGeneration, subscriptions));
    this.source = source;
    source.addEventListener("open", () => {
      if (this.source === source && !this.reconciling) {
        this.deps.onState({ kind: "live" });
      }
    });
    source.addEventListener("resync", (event) => {
      this.onResyncFrame(source, event.data);
    });
    for (const projection of this.positions.keys()) {
      source.addEventListener(projection, (event) => {
        this.onEventFrame(source, projection, event.data);
      });
    }
    source.onerror = () => {
      if (this.source === source) {
        this.resync("ERROR");
      }
    };
  }

  private onResyncFrame(source: EventSourceLike, data: string): void {
    if (this.source !== source) {
      return;
    }
    try {
      this.resync(decodeResyncFrame(JSON.parse(data)));
    } catch {
      this.resync("BAD_FRAME");
    }
  }

  private onEventFrame(source: EventSourceLike, projection: string, data: string): void {
    const position = this.positions.get(projection);
    if (this.source !== source || position === undefined) {
      return;
    }
    let event: StreamEvent;
    try {
      event = decodeStreamEvent(JSON.parse(data));
    } catch {
      this.resync("BAD_FRAME");
      return;
    }
    if (event.sequence <= position.watermark) {
      return;
    }
    if (!position.coalescing && event.sequence !== position.watermark + 1) {
      this.resync("GAP");
      return;
    }
    if (position.coalescing && event.version <= (position.versions.get(event.entityId) ?? 0)) {
      return;
    }
    position.watermark = event.sequence;
    this.remember(position, event);
    if (this.pending.length >= TRANSIENT_BUFFER_EVENTS) {
      this.resync("BUFFER_OVERFLOW");
      return;
    }
    this.pending.push(event);
    this.deps.onBuffered?.(this.pending.length);
    this.scheduleFlush();
  }

  private remember(position: Position, event: StreamEvent): void {
    position.versions.delete(event.entityId);
    position.versions.set(event.entityId, event.version);
    if (position.versions.size > DEDUP_ENTITIES_MAX) {
      for (const oldest of position.versions.keys()) {
        position.versions.delete(oldest);
        break;
      }
    }
  }

  private scheduleFlush(): void {
    if (this.flushing) {
      return;
    }
    this.flushing = true;
    this.deps.schedule(() => {
      this.flushing = false;
      const batch = this.pending;
      this.pending = [];
      for (const event of batch) {
        this.deps.onEvent(event);
      }
    }, 0);
  }

  /** Close the transport, mark every dependent view disconnected, and reconcile with a new snapshot. */
  resync(cause: Cause): void {
    if (this.closed || this.reconciling) {
      return;
    }
    this.reconciling = true;
    this.pending = [];
    this.closeSource();
    void this.reconcile(cause, 0);
  }

  private async reconcile(cause: Cause, attempt: number): Promise<void> {
    this.deps.onState({ kind: "reconciling", cause, attempt });
    let subscriptions: readonly Subscription[] | null;
    try {
      subscriptions = await this.deps.snapshot();
    } catch {
      this.retry(cause, attempt);
      return;
    }
    if (this.closed || subscriptions === null || this.runGeneration !== this.deps.generation()) {
      this.reconciling = false;
      return;
    }
    this.reconciling = false;
    this.connect(subscriptions);
  }

  private retry(cause: Cause, attempt: number): void {
    const delay = Math.min(RETRY_BASE_MS * 2 ** attempt, RETRY_MAX_MS);
    this.cancelTimer = this.deps.schedule(() => {
      this.cancelTimer = null;
      if (!this.closed) {
        void this.reconcile(cause, attempt + 1);
      }
    }, delay);
  }

  private closeSource(): void {
    if (this.source !== null) {
      this.source.onerror = null;
      this.source.close();
      this.source = null;
    }
  }

  /** Release the transport, the slot and any timer; the client is finished. */
  dispose(): void {
    if (this.closed) {
      return;
    }
    this.closed = true;
    this.closeSource();
    this.cancelTimer?.();
    this.pending = [];
    if (this.held) {
      this.deps.registry.release();
      this.held = false;
    }
    this.deps.onState({ kind: "idle" });
  }
}
